/**
 * MarcusActionTimeline — horizontal stepper showing Marcus's announced
 * plan. Ported from SagaActionTimeline with marcus-plan-* events.
 *
 * Listens for:
 *   marcus-plan-announced  { steps: [{label, target?}, ...] }
 *   marcus-plan-advance    { target?: string; index?: number; labelHint?: string }
 */
import React, { useEffect, useState } from 'react';
import { Check, ChevronRight, Loader2 } from 'lucide-react';
import { appEvents } from '@/shared/lib/events';

interface PlanStep {
  label: string;
  target?: string;
}

interface AnnouncedPlan {
  steps: PlanStep[];
  activeIndex: number;
}

const CLEAR_AFTER_MS = 8000;
const PLAN_STORAGE_KEY = 'marcus-action-plan';
const PLAN_TTL_MS = 30 * 60_000;
const HIDDEN_PLAN_TARGETS = new Set(['marcus-onboarding-next-step']);

interface PersistedPlan {
  plan: AnnouncedPlan;
  savedAt: number;
}

function normalizePlan(plan: AnnouncedPlan): AnnouncedPlan | null {
  const steps = plan.steps.filter((step) => !step.target || !HIDDEN_PLAN_TARGETS.has(step.target));
  if (steps.length === 0) return null;
  return {
    steps,
    activeIndex: Math.min(Math.max(plan.activeIndex, 0), steps.length - 1),
  };
}

function loadPersistedPlan(): AnnouncedPlan | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PLAN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedPlan;
    if (typeof parsed?.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > PLAN_TTL_MS) {
      window.localStorage.removeItem(PLAN_STORAGE_KEY);
      return null;
    }
    if (!Array.isArray(parsed.plan?.steps) || parsed.plan.steps.length === 0) return null;
    return normalizePlan(parsed.plan);
  } catch {
    return null;
  }
}

function persistPlan(plan: AnnouncedPlan | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (plan === null) {
      window.localStorage.removeItem(PLAN_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      PLAN_STORAGE_KEY,
      JSON.stringify({ plan, savedAt: Date.now() } satisfies PersistedPlan),
    );
  } catch { /* swallow quota errors */ }
}

export function MarcusActionTimeline() {
  const [plan, setPlan] = useState<AnnouncedPlan | null>(() => loadPersistedPlan());
  const [clearTimer, setClearTimer] = useState<number | null>(null);

  useEffect(() => {
    const unsubscribeAnnounced = appEvents.subscribe('marcus-plan-announced', (event) => {
      if (!event.steps.length) return;
      if (clearTimer) window.clearTimeout(clearTimer);
      const next = normalizePlan({ steps: event.steps, activeIndex: 0 });
      setPlan(next);
      persistPlan(next);
    });

    const unsubscribeAdvance = appEvents.subscribe('marcus-plan-advance', (event) => {
      setPlan((p) => {
        if (!p) return p;
        let next: number;
        if (typeof event.index === 'number') {
          next = event.index;
        } else if (event.target) {
          const found = p.steps.findIndex((s) => s.target === event.target);
          if (found >= 0) next = found;
          else next = Math.min(p.activeIndex + 1, p.steps.length - 1);
        } else if (event.labelHint) {
          const hint = event.labelHint.toLowerCase();
          const matches = p.steps
            .map((s, i) => ({ i, label: s.label.toLowerCase() }))
            .filter((s) => s.label.includes(hint));
          const forward = matches.find((m) => m.i >= p.activeIndex);
          if (forward) next = forward.i;
          else if (matches.length > 0) next = matches[matches.length - 1].i;
          else next = Math.min(p.activeIndex + 1, p.steps.length - 1);
        } else {
          next = Math.min(p.activeIndex + 1, p.steps.length - 1);
        }
        const t = window.setTimeout(() => {
          setPlan(null);
          persistPlan(null);
        }, CLEAR_AFTER_MS);
        setClearTimer(t);
        const updated = { ...p, activeIndex: next };
        persistPlan(updated);
        return updated;
      });
    });

    return () => {
      unsubscribeAnnounced();
      unsubscribeAdvance();
      if (clearTimer) window.clearTimeout(clearTimer);
    };
  }, [clearTimer]);

  if (!plan) return null;

  return (
    <div className="rounded-lg border border-border/40 bg-muted/15 p-2.5 space-y-1.5 text-[11px]">
      <div className="flex items-center gap-1.5 flex-wrap">
        {plan.steps.map((step, i) => {
          const done = i < plan.activeIndex;
          const active = i === plan.activeIndex;
          return (
            <React.Fragment key={i}>
              <span
                className={
                  active
                    ? 'inline-flex items-center gap-1 text-teal-300 font-medium'
                    : done
                    ? 'inline-flex items-center gap-1 text-emerald-400'
                    : 'inline-flex items-center gap-1 text-muted-foreground/70'
                }
              >
                {done ? (
                  <Check className="w-3 h-3" />
                ) : active ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <span className="inline-block w-3 h-3 rounded-full bg-muted/60 text-[8px] text-center leading-3">
                    {i + 1}
                  </span>
                )}
                <span>{step.label}</span>
              </span>
              {i < plan.steps.length - 1 && (
                <ChevronRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default MarcusActionTimeline;
